import React, { useState, useEffect } from "react";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { CyberpunkThemeProvider } from "@/context/CyberpunkThemeContext";
import { LicenseProvider } from "@/components/LicenseProvider";
import { CryptoProvider } from "@/state/CryptoContext";
import { MainNavigation } from "@/components/MainNavigation";
import { Footer } from "@/components/Footer";
import { CartProvider } from "./state/CartContext";
import { CartDrawer } from "./components/CartDrawer";
import RegisterRouter from "./components/RegisterRouter";
import FloatingCartButton from "./components/FloatingCartButton";
import i18n from './i18n';
import ScrollToTop from "./components/ScrollToTop";
import { useTranslation } from 'react-i18next';

import Home from "@/pages/Home";
import NotFound from "@/pages/not-found";
import VideosPage from "./pages/Videos";
import BlogPage from "./pages/blog";
import BlogPostPermalinkPage from "./pages/blog/[...slug]";

function ErrorBoundary({ children }: { children: React.ReactNode }) {
  const [error, setError] = useState<Error | null>(null);
  const { t } = useTranslation();
  React.useEffect(() => {
    const handler = (event: ErrorEvent) => {
      setError(event.error || new Error(event.message));
    };
    window.addEventListener('error', handler);
    return () => window.removeEventListener('error', handler);
  }, []);
  if (error) {
    return (
      <div style={{ background: '#0C1632', color: '#14F195', padding: 32, borderRadius: 16, margin: 32, fontSize: 18, fontWeight: 700, textAlign: 'center' }}>
        <h2>{t('errorboundary.something_went_wrong_')}</h2>
        <pre style={{ color: '#fff', fontSize: 14, marginTop: 16 }}>{error.message}</pre>
        <p style={{ fontSize: 12, color: '#aaa' }}>{t('errorboundary.please_refresh_the_page_or_conta')}</p>
      </div>
    );
  }
  return <>{children}</>;
}

function App({ lang }: { lang: string }) {
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [registrationComplete, setRegistrationComplete] = useState(false);
  
  // Detect if we're on a mobile device
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Initial check
    checkIfMobile();
    
    // Add listener for window resize
    window.addEventListener('resize', checkIfMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  useEffect(() => {
    i18n.changeLanguage(lang);
  }, [lang]);

  // Dynamically update <html> lang and dir attributes
  useEffect(() => {
    document.documentElement.lang = lang;
    // Comprehensive list of RTL languages
    const rtlLangs = [
      "ar", // Arabic
      "fa", // Persian (Farsi)
      "ur", // Urdu
      "he", // Hebrew
      "dv", // Divehi
      "ps", // Pashto
      "yi", // Yiddish
      "sd", // Sindhi
      "syr", // Syriac
      "ug", // Uyghur
      "ku", // Kurdish
      "bal", // Baluchi
      "ks", // Kashmiri
      "lrc", // Northern Luri
      "mzn", // Mazanderani
      "pnb"  // Western Punjabi
    ];
    document.documentElement.dir = rtlLangs.includes(lang) ? "rtl" : "ltr";
  }, [lang]);

  // Defer Google Tag Manager (gtag.js) after app is interactive
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://www.googletagmanager.com/gtag/js?id=AW-17061710943";
    script.async = true;
    document.body.appendChild(script);

    const inlineScript = document.createElement("script");
    inlineScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'AW-17061710943');
    `;
    document.body.appendChild(inlineScript);
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <CryptoProvider>
          <CyberpunkThemeProvider>
            <TooltipProvider>
              <LicenseProvider>
                <CartProvider>
                  <div className="min-h-screen flex flex-col">
                    <ScrollToTop />
                    <MainNavigation />
                    <CartDrawer />
                    <Toaster />
                    <div className="flex-grow">
                      <Routes>
                        {/* Language-prefixed routes */}
                        <Route path="/:lang/" element={<Home />} />
                        <Route path="/:lang/videos" element={<VideosPage />} />
                        <Route path="/:lang/blog/:year/:month/:slug" element={<BlogPostPermalinkPage />} />
                        <Route path="/:lang/blog/*" element={<BlogPage />} />
                        <Route path="/:lang/register" element={
                          <div className="w-full flex flex-col items-center justify-center py-8 bg-[#0c0c15]">
                            <RegisterRouter onRegistrationSuccess={() => setRegistrationComplete(true)} />
                          </div>
                        } />
                        {/* Root routes for default language */}
                        <Route path="/" element={<Home />} />
                        <Route path="/videos" element={<VideosPage />} />
                        <Route path="/blog/:year/:month/:slug" element={<BlogPostPermalinkPage />} />
                        <Route path="/blog/*" element={<BlogPage />} />
                        <Route path="/register" element={
                          <div className="w-full flex flex-col items-center justify-center py-8 bg-[#0c0c15]">
                            <RegisterRouter onRegistrationSuccess={() => setRegistrationComplete(true)} />
                          </div>
                        } />
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </div>
                    <Footer />
                    <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-primary text-white p-2 z-50">
                      Skip to content
                    </a>
                  </div>
                </CartProvider>
              </LicenseProvider>
            </TooltipProvider>
          </CyberpunkThemeProvider>
        </CryptoProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
